import { createRouter, createWebHistory, type RouteRecordRaw, type Router } from 'vue-router';
import { useSession } from './stores/session';

declare module 'vue-router' {
  interface RouteMeta {
    /** Exige sessão de administrador. */
    auth?: boolean;
    /** Só para quem NÃO entrou. */
    guest?: boolean;
    title?: string;
  }
}

export const routes: RouteRecordRaw[] = [
  { path: '/login', name: 'login', component: () => import('./views/LoginView.vue'), meta: { guest: true, title: 'Entrar' } },
  { path: '/', redirect: { name: 'parameters' } },
  { path: '/parametros', name: 'parameters', component: () => import('./views/ParametersView.vue'), meta: { auth: true, title: 'Dano' } },
  {
    path: '/arremesso',
    name: 'physics',
    component: () => import('./views/ParametersView.vue'),
    props: { domain: 'physics' },
    meta: { auth: true, title: 'Arremesso e vento' },
  },
  {
    path: '/atributos',
    name: 'character',
    component: () => import('./views/ParametersView.vue'),
    props: { domain: 'character' },
    meta: { auth: true, title: 'Atributos' },
  },
  { path: '/simulador', name: 'simulator', component: () => import('./views/SimulatorView.vue'), meta: { auth: true, title: 'Simulador' } },
  {
    path: '/itens',
    component: () => import('./views/ItemsLayout.vue'),
    meta: { auth: true, title: 'Itens' },
    children: [
      { path: '', name: 'weapons', component: () => import('./views/WeaponsView.vue'), meta: { title: 'Armas' } },
      { path: 'arma', redirect: { name: 'weapons' } },
      { path: 'arma/nova', name: 'weapon-new', component: () => import('./views/WeaponWizardView.vue'), meta: { title: 'Nova arma' } },
      { path: 'arma/:id(\\d+)', name: 'weapon-bench', component: () => import('./views/WeaponBenchView.vue'), props: true, meta: { title: 'Bancada da arma' } },
      { path: 'arma/:id(\\d+)/editar', name: 'weapon-edit', component: () => import('./views/WeaponWizardView.vue'), props: true, meta: { title: 'Editar arma' } },
      { path: 'imagens', name: 'art-library', component: () => import('./views/ArtLibraryView.vue'), meta: { title: 'Imagens' } },
      { path: 'rodelas', name: 'rodelas', component: () => import('./views/RodelasView.vue'), meta: { title: 'Rodelas' } },
      { path: 'guia-de-imagens', name: 'image-guide', component: () => import('./views/ImageGuideView.vue'), meta: { title: 'Guia de imagens' } },
      { path: ':type', name: 'items-type', component: () => import('./views/ItemsView.vue'), props: (route) => ({ typeKey: String(route.params.type) }), meta: { title: 'Itens' } },
    ],
  },
  { path: '/bombas', name: 'balls', component: () => import('./views/TableView.vue'), props: { kind: 'balls' }, meta: { auth: true, title: 'Bombas' } },
  { path: '/habilidades', name: 'skills', component: () => import('./views/TableView.vue'), props: { kind: 'skills' }, meta: { auth: true, title: 'Habilidades' } },
  // As bombas de cada arma vivem em "Visual das armas"; o endereço antigo (e o `?q=` dele) leva para lá.
  { path: '/armas', redirect: (to) => ({ name: 'weapon-visuals', query: to.query }) },
  { path: '/visual-armas', name: 'weapon-visuals', component: () => import('./views/WeaponVisualsView.vue'), meta: { auth: true, title: 'Visual das armas' } },
  {
    path: '/missoes',
    component: () => import('./views/quests/QuestsLayout.vue'),
    meta: { auth: true, title: 'Missões' },
    children: [
      { path: '', name: 'quests', component: () => import('./views/quests/QuestListView.vue'), meta: { title: 'Missões' } },
      { path: 'nova', name: 'quest-new', component: () => import('./views/quests/QuestEditView.vue'), meta: { title: 'Nova missão' } },
      { path: 'objetivos', name: 'quest-objectives', component: () => import('./views/quests/ObjectivesView.vue'), meta: { title: 'Objetivos' } },
      { path: ':id(\\d+)', name: 'quest-edit', component: () => import('./views/quests/QuestEditView.vue'), meta: { title: 'Missão' } },
    ],
  },
  { path: '/historico', name: 'history', component: () => import('./views/HistoryView.vue'), meta: { auth: true, title: 'Histórico de balanceamento' } },
  {
    path: '/jogo',
    component: () => import('./views/admin/AdminLayout.vue'),
    meta: { auth: true, title: 'Operação do jogo' },
    children: [
      { path: '', redirect: { name: 'game-characters' } },
      { path: 'personagens', name: 'game-characters', component: () => import('./views/admin/AdminCharacters.vue'), meta: { title: 'Personagens' } },
      { path: 'contas', name: 'game-accounts', component: () => import('./views/admin/AdminAccounts.vue'), meta: { title: 'Contas' } },
      { path: 'correio', name: 'game-mail', component: () => import('./views/admin/AdminMail.vue'), meta: { title: 'Correio' } },
      { path: 'eventos', name: 'game-events', component: () => import('./views/admin/AdminEvents.vue'), meta: { title: 'Eventos' } },
      { path: 'auditoria', name: 'game-audit', component: () => import('./views/admin/AdminAudit.vue'), meta: { title: 'Auditoria do jogo' } },
    ],
  },
  // Endereço antigo da auditoria.
  { path: '/auditoria', redirect: { name: 'history' } },
  { path: '/:pathMatch(.*)*', name: 'not-found', component: () => import('./views/NotFoundView.vue'), meta: { title: 'Não encontrado' } },
];

export function createAppRouter(history = createWebHistory()): Router {
  const router = createRouter({ history, routes });

  // Não existe cadastro: tudo que não é o login exige uma sessão de administrador.
  router.beforeEach((to) => {
    const session = useSession();
    if (to.meta.auth && !(session.authenticated && session.isAdmin)) {
      return { name: 'login', query: { redirect: to.fullPath } };
    }
    if (to.meta.guest && session.authenticated) return { name: 'parameters' };
    return true;
  });

  router.afterEach((to) => {
    document.title = to.meta.title ? `${to.meta.title} · Painel admin` : 'O jogo · Painel admin';
  });

  return router;
}
